function normalizeSkillName(name) {
  if (!name) return '';
  let s = String(name).trim();
  s = s.replace(/'/g, '');
  s = s.replace(/\s*\([^)]*\)/g, '').trim();
  s = s.replace(/\s+/g, ' ');
  const upper = s.toUpperCase();
  if (upper.includes('1:8') || upper.includes('ONE EIGHT')) return 'ONE EIGHT';
  if (upper.includes('GIRL') && upper.includes('CLUB')) return 'GIRLS CLUB';
  if (upper.includes('MAN CAVE')) return 'MAN CAVE';
  if (upper.includes('KAVA ONLY')) return 'KAVA ONLY';
  if (upper.includes('GET MESSY')) return 'GET MESSY';
  if (upper.includes('MASU MADNESS')) return 'MASU MADNESS';
  if (upper.includes('UNDER COVER') || upper.includes('UNDERCOVER')) return 'UNDER COVER';
  if (upper.includes('ON THE LAWN')) return 'ON THE LAWN';
  if (upper.includes('BUILD IT UP')) return 'BUILD IT UP';
  if (upper.includes('GET CRAFTY')) return 'CRAFTS';
  if (/\bCRAFTS?\b/.test(upper)) return 'CRAFTS';
  if (upper.includes('EPIC EATS')) return 'EPIC EATS';
  return upper;
}

function normalizeCamperName(name) {
  return String(name || '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

function parseGrade(value) {
  const lower = String(value || '').trim().toLowerCase();
  if (!lower) return null;
  if (lower.startsWith('k')) return 0;
  const match = lower.match(/(\d+)/);
  return match ? parseInt(match[1], 10) : null;
}

function extractPreferencesFromRow(row) {
  const preferenceEntries = Object.keys(row || {}).map((key) => {
    const lower = key.toLowerCase();
    const match = lower.match(/(\d+)(st|nd|rd|th)/);
    if (!lower.includes('skill') && !lower.includes('request')) return null;
    if (!match) return null;
    const rank = parseInt(match[1], 10);
    const raw = row[key];
    if (!raw) return null;
    return { rank, skill: normalizeSkillName(raw) };
  }).filter(Boolean);
  preferenceEntries.sort((a, b) => a.rank - b.rank);
  const deduped = [];
  const seen = new Set();
  preferenceEntries.forEach(({ skill }) => {
    if (!skill || seen.has(skill)) return;
    seen.add(skill);
    deduped.push(skill);
  });
  return deduped;
}

function buildRegistrationMap(registrationRows) {
  const map = new Map();
  (registrationRows || []).forEach((row) => {
    const name = row['Participant: Name'] || row.Name || row.name;
    if (!name) return;
    const normalizedName = normalizeCamperName(name);
    map.set(normalizedName, {
      name: String(name).trim(),
      gender: row['Participant: Gender'] || '',
      grade: parseGrade(row['Participant: Grade'] || row.Grade || row.grade),
      preferences: extractPreferencesFromRow(row),
    });
  });
  return map;
}

function buildAssignmentMap(scheduleRows) {
  const map = new Map();
  (scheduleRows || []).forEach((row) => {
    const firstName = row.Name || row.name || '';
    const secondaryName = row[''] || row._1 || row.Last || row['Last Name'] || row.last || '';
    const noteColumn = row._2 || '';
    const name = [firstName, secondaryName]
      .map((value) => String(value || '').trim())
      .filter(Boolean)
      .join(' ');
    if (!name) return;
    if (!row['Rotation 1'] && !row['Rotation 2'] && !row['Rotation 3'] && !row['Rotation 4'] && !row['Rotation 5'] && !row['Rotation 6']) return;
    if (/only change on this sheet/i.test(noteColumn)) {
      // Data rows can still carry the instruction text in a duplicate column; ignore the column.
    }
    const rotations = [];
    for (let i = 1; i <= 6; i += 1) {
      rotations.push(normalizeSkillName(row[`Rotation ${i}`] || row[`rotation ${i}`] || ''));
    }
    map.set(normalizeCamperName(name), {
      name: String(name).trim(),
      rotations,
    });
  });
  return map;
}

export function analyzeScheduleQuality(registrationRows, scheduleRows, options = {}) {
  const skillConfig = options.skillConfig || {};
  const registrationMap = buildRegistrationMap(registrationRows);
  const assignmentMap = buildAssignmentMap(scheduleRows);

  const registrationNames = new Set(registrationMap.keys());
  const assignmentNames = new Set(assignmentMap.keys());
  const matchedNames = Array.from(registrationNames).filter((name) => assignmentNames.has(name));
  const missingFromSchedule = Array.from(registrationNames).filter((name) => !assignmentNames.has(name));
  const extraInSchedule = Array.from(assignmentNames).filter((name) => !registrationNames.has(name));

  const preferenceHits = {};
  for (let i = 1; i <= 10; i += 1) preferenceHits[i] = 0;
  preferenceHits.none = 0;
  preferenceHits.excluded = 0;

  const perRotation = Array.from({ length: 6 }, (_, index) => ({
    rotation: index + 1,
    filled: 0,
    blank: 0,
    requested: 0,
    notRequested: 0,
    excluded: 0,
  }));
  const skillCounts = {};
  let filledSlots = 0;
  let missingSlots = 0;
  let repeatedSkillViolations = 0;
  let requestedAssignments = 0;
  let notRequestedAssignments = 0;
  let excludedAssignments = 0;
  let campersFullyPlaced = 0;
  let campersWithAnyBlank = 0;
  let campersAllRequested = 0;
  let campersWithTop3 = 0;
  let campersWithTop5 = 0;
  let preferencePenalty = 0;
  const camperRows = [];

  matchedNames.forEach((normalizedName) => {
    const registration = registrationMap.get(normalizedName);
    const assignment = assignmentMap.get(normalizedName);
    const seenSkills = new Set();
    let blanks = 0;
    let allRequested = true;
    let hasTop3 = false;
    let hasTop5 = false;
    const rotationDetails = assignment.rotations.map((skill, index) => {
      const isExcludedFromCounts = !!skill && (
        skillConfig?.[skill]?.requiredForEligible === true
        || skillConfig?.[skill]?.repeatable === true
      );
      const requestedIndex = registration.preferences.findIndex((pref) => pref === skill);
      const requested = !!skill && requestedIndex >= 0;
      const preferenceRank = requested ? requestedIndex + 1 : null;
      if (!skill) {
        blanks += 1;
        missingSlots += 1;
        perRotation[index].blank += 1;
        allRequested = false;
      } else {
        filledSlots += 1;
        perRotation[index].filled += 1;
        skillCounts[skill] = (skillCounts[skill] || 0) + 1;
        if (isExcludedFromCounts) {
          excludedAssignments += 1;
          perRotation[index].excluded += 1;
          preferenceHits.excluded += 1;
        } else if (requested) {
          requestedAssignments += 1;
          perRotation[index].requested += 1;
          preferenceHits[preferenceRank] += 1;
          preferencePenalty += preferenceRank;
          if (preferenceRank <= 3) hasTop3 = true;
          if (preferenceRank <= 5) hasTop5 = true;
        } else {
          notRequestedAssignments += 1;
          perRotation[index].notRequested += 1;
          preferenceHits.none += 1;
          allRequested = false;
        }
        if (!isExcludedFromCounts && seenSkills.has(skill)) {
          repeatedSkillViolations += 1;
        }
        seenSkills.add(skill);
      }
      return {
        rotation: index + 1,
        skill,
        requested,
        preferenceRank,
        excludedFromCounts: isExcludedFromCounts,
      };
    });
    if (blanks === 0) campersFullyPlaced += 1;
    else campersWithAnyBlank += 1;
    if (allRequested && blanks === 0) campersAllRequested += 1;
    if (hasTop3) campersWithTop3 += 1;
    if (hasTop5) campersWithTop5 += 1;
    camperRows.push({
      name: registration.name,
      grade: registration.grade,
      blanks,
      allRequested,
      topPreferenceHit: rotationDetails.reduce((best, detail) => {
        if (!detail.preferenceRank) return best;
        return best == null ? detail.preferenceRank : Math.min(best, detail.preferenceRank);
      }, null),
      requestedSkillsRemaining: registration.preferences.filter((pref) => !seenSkills.has(pref)),
      rotations: rotationDetails,
    });
  });

  const matchedCount = matchedNames.length;
  const avgPreferenceRank = requestedAssignments > 0 ? preferencePenalty / requestedAssignments : null;
  const sortedSkillCounts = Object.entries(skillCounts)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([skill, count]) => ({ skill, count }));

  return {
    summary: {
      registrationCampers: registrationMap.size,
      scheduleCampers: assignmentMap.size,
      matchedCampers: matchedCount,
      missingFromSchedule: missingFromSchedule.length,
      extraInSchedule: extraInSchedule.length,
      campersFullyPlaced,
      campersWithAnyBlank,
      filledSlots,
      missingSlots,
      requestedAssignments,
      notRequestedAssignments,
      excludedAssignments,
      repeatedSkillViolations,
      avgPreferenceRank,
      campersAllRequested,
      campersWithTop3,
      campersWithTop5,
    },
    preferenceHits,
    perRotation,
    skillCounts: sortedSkillCounts,
    missingCampers: missingFromSchedule.map((name) => registrationMap.get(name)?.name || name),
    extraCampers: extraInSchedule.map((name) => assignmentMap.get(name)?.name || name),
    camperRows,
  };
}
